import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CreditCard, Users, Home, LogOut, Calendar, Clock, MessageSquare, X, ChevronDown, Bell, MessageCircle, AlertTriangle, Video } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReportSessionModal from "@/components/ReportSessionModal";
import TokenPurchaseModal from "@/components/CreditPurchaseModal";

import { Link, useNavigate } from "react-router-dom";
import MentorScheduleViewer from "@/components/MentorScheduleViewer";
import StreamlinedBookingFlow from "@/components/StreamlinedBookingFlow";
import { SessionReviewModal } from "@/components/SessionReviewModal";
import { useStudentCredits } from "@/hooks/useStudentCredits";
import { NotificationBell } from "@/components/NotificationBell";
import { AccountDeactivation } from "@/components/AccountDeactivation";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import MentorCarousel from "@/components/MentorCarousel";
import { formatTime12Hour, cn } from "@/lib/utils";
import { signOutGlobally, handleBookingContinuation, clearBookingData, storeBookingData } from "@/lib/auth-utils";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@/lib/safe-storage";
import { format } from "date-fns";
import { getUserTimezone, getStoredTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";
import { AIMentorChatbot } from "@/components/AIMentorChatbot";
import ComplianceFooter from "@/components/ComplianceFooter";
import { ProgramSessionSelector } from "@/components/ProgramSessionSelector";
import { StudentProgramEnrollments } from "@/components/StudentProgramEnrollments";
import { trackPurchase, trackInitiateCheckout } from "@/lib/meta-pixel";
import { RecommendedMentorsSection } from "@/components/RecommendedMentorsSection";

interface Mentor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  specialty: string;
  rating: number;
  hourly_rate: number;
  expertise: string[];
  bio: string;
  is_active: boolean;
  total_sessions?: number;
  discovery_call_enabled?: boolean;
}

const StudentDashboard = () => {
  // ALL HOOKS MUST BE AT THE TOP - NO EARLY RETURNS BEFORE ALL HOOKS
  
  // Set page title
  useEffect(() => {
    document.title = "Student Dashboard - Ascendry";
  }, []);
  
  const [studentProfile, setStudentProfile] = useState<{first_name: string; last_name: string; user_id: string; email: string; role?: string} | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<any | null>(null);
  const [bookingNotes, setBookingNotes] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<any>(null);
  const [realMentors, setRealMentors] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [wasBookingInProgress, setWasBookingInProgress] = useState(false);
  const [reviewModalSession, setReviewModalSession] = useState<any>(null);
  const [sessionToCancel, setSessionToCancel] = useState<any>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [sessionToReschedule, setSessionToReschedule] = useState<any>(null);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);
  const [sessionToReport, setSessionToReport] = useState<any>(null);
  const [isPreparationNoticeDismissed, setIsPreparationNoticeDismissed] = useState(() => 
    safeGetItem('preparation_notice_dismissed') === 'true'
  );
  const [selectedProgramEnrollmentId, setSelectedProgramEnrollmentId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Use the credits hook
  const { credits, loading: creditsLoading, refetch: refetchCredits, processPaymentSuccess } = useStudentCredits(studentProfile?.user_id);

  // Handle payment success callback from Stripe
  useEffect(() => {
    const processStripeCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const sessionId = urlParams.get('session_id');
      
      console.log('Payment callback detected:', { paymentStatus, sessionId });
      
      // Handle canceled payments
      if (paymentStatus === 'canceled') {
        window.history.replaceState({}, '', '/dashboard/student');
        toast({
          title: "Payment Canceled",
          description: "Your payment was canceled. No charges were made.",
        });
        return;
      }
      
      if (paymentStatus === 'success') {
        // Clear the URL parameters immediately to prevent double processing
        window.history.replaceState({}, '', '/dashboard/student');

        // Get user ID if available (not required for processing)
        const { data: { session } } = await supabase.auth.getSession();
        console.log('🔵 Current user (optional):', session?.user?.id);

        try {
          if (sessionId) {
            console.log('🔵 Invoking process-payment-success edge function with sessionId...');
            const { data, error } = await supabase.functions.invoke('process-payment-success', {
              body: { sessionId }
            });
            console.log('🔵 Edge function raw response:', { data, error });
            if (error) throw error;
            if (!data?.success) throw new Error(data?.error || 'Payment processing failed - invalid response');
            console.log('✅ Payment processed successfully, credits added:', data.credits);
            
            // Track purchase with Meta Pixel
            const purchaseAmount = data.amount || 0; // Amount in dollars
            trackPurchase(purchaseAmount, 'USD');
            
            toast({ title: 'Payment Successful!', description: `${data.credits} tokens have been added to your account.` });
          } else {
            console.log('🟣 session_id missing, attempting recovery...');
            const { data, error } = await supabase.functions.invoke('recover-latest-payment');
            console.log('🟣 Recovery response:', { data, error });
            if (error) throw error;
            if (!data?.success && !data?.alreadyProcessed) throw new Error('Recovery failed');
            if (data?.success && data?.credits > 0) {
              // Track recovered purchase with Meta Pixel
              const purchaseAmount = data.amount || 0;
              trackPurchase(purchaseAmount, 'USD');
              
              toast({ title: 'Payment Recovered', description: `${data.credits} tokens have been added to your account.` });
            } else if (data?.alreadyProcessed) {
              toast({ title: 'Already Credited', description: 'Your purchase was already applied.' });
            }
          }

          // Refresh credits after successful payment
          setTimeout(() => {
            console.log('🔄 Refreshing credit balance...');
            refetchCredits();
          }, 1000);
        } catch (error) {
          console.error('❌ process-payment-success failed, attempting recovery...', error);
          try {
            const { data: recData, error: recErr } = await supabase.functions.invoke('recover-latest-payment');
            console.log('🟣 Recovery after failure:', { recData, recErr });
            
            // Handle "no payment found" gracefully
            if (recErr?.message?.includes('No completed payment found') || recData?.message?.includes('No completed payment found')) {
              console.log('ℹ️ No payment history to recover during fallback');
              // Don't show success toast, fall through to error toast below
            } else if (!recErr && (recData?.success || recData?.alreadyProcessed)) {
              if (recData?.success && recData?.credits > 0) {
                // Track recovered purchase with Meta Pixel
                const purchaseAmount = recData.amount || 0;
                trackPurchase(purchaseAmount, 'USD');
                
                toast({ title: 'Payment Recovered', description: `${recData.credits} tokens have been added to your account.` });
              }
              setTimeout(() => {
                console.log('🔄 Refreshing credit balance after recovery...');
                refetchCredits();
              }, 1000);
              return; // ✅ Suppress error toast because recovery succeeded
            }
          } catch (recoverErr) {
            console.warn('Recovery attempt failed as well:', recoverErr);
          }
          // If both primary processing and recovery failed, show error
          toast({
            title: 'Payment Processing Error',
            description: 'Payment was successful but tokens could not be applied automatically. Please contact support with your order details.',
            variant: 'destructive',
          });
        }
      }
    };
    
    processStripeCallback();
  }, [toast, refetchCredits]); // Remove studentProfile dependency

  // For testing - bypass loading for admins
  React.useEffect(() => {
    const initializeForAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role, first_name, last_name, user_id, email')
          .eq('user_id', session.user.id)
          .single();
        
        if (profile) {
          setStudentProfile(profile);
          setIsProfileLoading(false);
        }
      }
    };
    
    initializeForAdmin();
  }, []);

  // All other useEffect hooks must also be at the top
  useEffect(() => {
    console.log('StudentDashboard useEffect running...');
    
    const fetchData = async () => {
      try {
        console.log('Starting fetchData...');
        
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Session:', session);
        
        if (!session?.user) {
          console.log('No session, redirecting to auth');
          navigate('/auth/student');
          return;
        }

        // Fetch and set student profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('role, first_name, last_name, user_id, email')
          .eq('user_id', session.user.id)
          .single();
        
        if (profile) {
          console.log('📋 Profile fetched:', profile);
          setStudentProfile(profile);
          setIsProfileLoading(false); // ✅ Mark loading complete
        }

        // Check for direct booking request from mentor profile
        const startBookingMentorData = safeGetItem('startBookingForMentor');
        if (startBookingMentorData) {
          safeRemoveItem('startBookingForMentor');
          try {
            const mentorToBook = JSON.parse(startBookingMentorData);
            setSelectedMentor(mentorToBook);
          } catch (error) {
            console.error('Error parsing mentor booking data:', error);
          }
        }
        
        // Clear any existing booking data to prevent automatic sessions
        clearBookingData();
        safeRemoveItem('pendingMentorBooking');
        safeRemoveItem('pendingBooking');
        safeRemoveItem('bookingNotes');
        safeRemoveItem('selectedTimeSlot');
        safeRemoveItem('selectedMentorId');

        // Check for payment success and show notification
        const urlParams = new URLSearchParams(window.location.search);
        const paymentStatus = urlParams.get('payment');
        const sessionId = urlParams.get('session_id');
        
        if (paymentStatus === 'success' && sessionId) {
          // Process payment logic here if needed
        }

        // Fetch mentors and sessions if needed
        const fetchMentors = async () => {
          try {
            const { data, error } = await supabase
              .from('mentors')
              .select('id, first_name, last_name, specialty, expertise, credits, profile_image_url, tier, hourly_rate, rating, total_sessions, discovery_call_enabled');
            
            if (error) throw error;
            
            // Enhanced sorting: BLAQOUT first, then rated mentors by rating/sessions, unrated alphabetically
            const sortedMentors = (data || []).sort((a, b) => {
              // BLAQOUT always comes first
              if (a.first_name === 'BLAQOUT') return -1;
              if (b.first_name === 'BLAQOUT') return 1;
              
              // If both have ratings > 0, sort by rating then sessions
              if (a.rating > 0 && b.rating > 0) {
                if (a.rating !== b.rating) return b.rating - a.rating;
                return b.total_sessions - a.total_sessions;
              }
              // If only one has rating, rated mentor comes first
              if (a.rating > 0) return -1;
              if (b.rating > 0) return 1;
              // If neither has rating, alphabetize by first name
              return a.first_name.localeCompare(b.first_name);
            });
            
            setRealMentors(sortedMentors);
          } catch (error) {
            console.error('Error fetching mentors:', error);
          }
        };

        const fetchSessions = async () => {
          try {
            const { data, error } = await supabase
              .from('sessions')
              .select(`
                *,
                mentors (id, first_name, last_name, specialty, profile_image_url, email, hourly_rate),
                session_reviews (id, rating, review_text)
              `)
              .eq('student_id', session.user.id)
              .neq('status', 'cancelled') // Filter out cancelled sessions
              .order('scheduled_at', { ascending: true });
            
            if (error) throw error;
            setSessions(data || []);
          } catch (error) {
            console.error('Error fetching sessions:', error);
          }
        };

        fetchMentors();
        fetchSessions();

      } catch (error) {
        console.error('Error in fetchData:', error);
        setIsProfileLoading(false); // ✅ Mark loading complete even on error
      }
    };

    fetchData();
  }, []);

  // Silent recovery: try when balance appears 0; idempotent on the backend
  const triedRecoveryRef = useRef(false);
  useEffect(() => {
    const run = async () => {
      if (triedRecoveryRef.current) return;
      try {
        const { data: auth } = await supabase.auth.getSession();
        if (!auth?.session) return; // only when authenticated

        // If we're in an explicit payment callback, let the dedicated handler manage it
        const params = new URLSearchParams(window.location.search);
        const paymentStatus = params.get('payment');
        if (paymentStatus === 'success' || paymentStatus === 'canceled') return;

        // Only attempt if balance shows as 0 / undefined
        if (typeof credits?.balance === 'number' && credits.balance > 0) return;

        triedRecoveryRef.current = true;
        const { data, error } = await supabase.functions.invoke('recover-latest-payment');
        console.log('🟣 Background recovery attempt:', { data, error });

        // Handle success or already processed
        if (!error && (data?.success || data?.alreadyProcessed)) {
          if (data?.success && data?.credits > 0) {
            toast({ title: 'Payment Recovered', description: `${data.credits} tokens have been added to your account.` });
          }
          setTimeout(() => refetchCredits(), 800);
        } else if (error?.message?.includes('No completed payment found') || data?.message?.includes('No completed payment found')) {
          // Expected for users with no payment history - silently ignore
          console.log('ℹ️ No payment history to recover (expected for new users)');
        } else {
          // Allow a subsequent attempt on next mount/navigation
          triedRecoveryRef.current = false;
        }
      } catch (e) {
        console.warn('Silent recovery not applicable:', e);
      }
    };
    run();
  }, [credits?.balance, refetchCredits, toast]);

  // NOW we can do early returns after ALL hooks have been declared
  if (isProfileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg">Loading dashboard...</div>
          <div className="text-sm text-muted-foreground mt-2">Fetching your profile data</div>
        </div>
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      // Clear any booking data on sign out
      clearBookingData();
      
      await signOutGlobally();
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePurchase = async (packageId: string, amount: number) => {
    console.log('=== Starting payment flow ===');
    console.log('Package ID:', packageId, 'Amount:', amount);
    
    try {
      // Ensure we have a valid session before calling edge function
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error("Please sign in again to continue");
      }

      console.log('Invoking create-payment edge function...');
      
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          packageId,
          returnUrl: window.location.origin + '/dashboard/student'
        }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (data?.error) {
        console.error('Payment creation error from server:', data.error);
        throw new Error(data.error);
      }

      if (data?.url) {
        console.log('Redirecting to Stripe checkout:', data.url);
        // Redirect in same window so success callback works correctly
        window.location.href = data.url;
      } else {
        console.error('No checkout URL received:', data);
        throw new Error('No checkout URL received from payment service');
      }
    } catch (error) {
      console.error('=== Payment flow error ===');
      console.error('Error details:', error);
      
      toast({
        title: "Payment Error",
        description: error instanceof Error ? error.message : "Failed to create payment session. Please try again or contact support.",
        variant: "destructive",
      });
    }
  };

  const downloadCalendarFile = async (sessionId: string) => {
    try {
      // Make direct fetch to edge function to handle file download
      const response = await fetch(`https://llcojpvgertoimsualob.supabase.co/functions/v1/generate-calendar-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I'
        },
        body: JSON.stringify({ sessionId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Calendar generation failed: ${errorText}`);
      }

      // Get the file content
      const icsContent = await response.text();
      
      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mentoring-session-${sessionId}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Calendar File Downloaded",
        description: "The .ics file has been downloaded to your device.",
      });
    } catch (error: any) {
      console.error('Calendar download error details:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Failed to generate calendar file.",
        variant: "destructive",
      });
    }
  };

  const handleBookSession = (mentor: any) => {
    // Store booking data in case user needs to authenticate
    storeBookingData(mentor.id);
    setSelectedMentor(mentor);
  };

  const handleCancelSession = (session: any) => {
    setSessionToCancel(session);
    setShowCancelDialog(true);
  };

  const confirmCancelSession = async () => {
    if (!sessionToCancel || !studentProfile) return;

    try {
      const sessionDate = new Date(sessionToCancel.scheduled_at);
      const today = new Date();
      const isDayOf = sessionDate.toDateString() === today.toDateString();

      // Use the robust cancellation function that handles both status update and refund atomically
      const { data: result, error: cancelError } = await supabase.rpc('cancel_session_with_refund', {
        p_session_id: sessionToCancel.id,
        p_user_id: studentProfile.user_id,
        p_is_day_of: isDayOf
      });

      if (cancelError) {
        throw new Error(`Failed to cancel session: ${cancelError.message}`);
      }

      // Type assertion for the result
      const cancelResult = result as { success: boolean; error?: string; refund_processed?: boolean } | null;

      if (!cancelResult?.success) {
        throw new Error(cancelResult?.error || 'Failed to cancel session');
      }

      console.log('Session cancellation result:', cancelResult);

      // Send cancellation email (don't fail if this fails)
      try {
        await supabase.functions.invoke('send-cancellation-email', {
          body: {
            sessionId: sessionToCancel.id,
            mentorName: `${sessionToCancel.mentors.first_name} ${sessionToCancel.mentors.last_name}`,
            mentorEmail: sessionToCancel.mentors.email || 'mentor@ascendry.app',
            studentName: `${studentProfile.first_name} ${studentProfile.last_name}`,
            studentEmail: studentProfile.email,
            scheduledAt: sessionToCancel.scheduled_at,
            notes: sessionToCancel.notes,
            cancelledBy: 'student',
            refundGiven: cancelResult.refund_processed || false
          }
        });
      } catch (emailError) {
        console.error('Error sending cancellation email:', emailError);
        // Don't fail the cancellation if email fails
      }

      // Update UI with proper messaging
      toast({
        title: "Session cancelled successfully",
        description: cancelResult.refund_processed 
          ? "Session cancelled and credits refunded to your account."
          : "Session cancelled. Credits were forfeited due to day-of cancellation.",
      });

      // Force refresh all data to ensure UI is in sync
      setSessions(prev => prev.filter(s => s.id !== sessionToCancel.id));
      refetchCredits();
      
      // Also refresh sessions from server to be absolutely sure
      setTimeout(async () => {
        try {
          const { data } = await supabase
            .from('sessions')
            .select(`
              *,
              mentors (first_name, last_name, specialty, profile_image_url, email, hourly_rate)
            `)
            .eq('student_id', studentProfile.user_id)
            .neq('status', 'cancelled') // Filter out cancelled sessions
            .order('scheduled_at', { ascending: true });
          setSessions(data || []);
        } catch (error) {
          console.error('Error refreshing sessions:', error);
        }
      }, 1000);

    } catch (error: any) {
      console.error('Error cancelling session:', error);
      toast({
        title: "Cancellation Failed", 
        description: error.message || "Failed to cancel session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowCancelDialog(false);
      setSessionToCancel(null);
    }
  };

  const getSessionDate = () => {
    if (!sessionToCancel) return false;
    const sessionDate = new Date(sessionToCancel.scheduled_at);
    const today = new Date();
    return sessionDate.toDateString() === today.toDateString();
  };

  const confirmBooking = async () => {
    if (isBookingInProgress) return; // Prevent multiple submissions
    
    if (!bookingNotes.trim()) {
      toast({
        title: "Missing Information",
        description: "Please add notes for your mentor about what you'd like to discuss.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedTimeSlot) {
      toast({
        title: "Missing Time Slot",
        description: "Please select a date and time for your session.",
        variant: "destructive",
      });
      return;
    }

    // Check if using program session or regular tokens
    const usingProgramSession = !!selectedProgramEnrollmentId;
    const tokensNeeded = selectedMentor ? (selectedMentor.hourly_rate / 100) : 10;
    
    // Only check token balance if NOT using a program session (and not rescheduling)
    if (!sessionToReschedule && !usingProgramSession && credits.balance < tokensNeeded) {
      toast({
        title: "Tokens Required",
        description: `You need at least ${tokensNeeded} tokens to book a session. Please purchase tokens to continue.`,
        variant: "destructive",
      });
      setWasBookingInProgress(true);
      setIsPurchaseModalOpen(true);
      return;
    }

    setIsBookingInProgress(true);
    
    // Track session booking initiation with Meta Pixel
    trackInitiateCheckout();
    
    try {
      let sessionData;

      if (sessionToReschedule) {
        // For reschedules after the first one, charge 1 token
        const currentRescheduleCount = sessionToReschedule.reschedule_count || 0;
        const isSecondOrLaterReschedule = currentRescheduleCount >= 1;
        
        if (isSecondOrLaterReschedule) {
          // Check if user has enough tokens for the reschedule fee
          if (credits.balance < 1) {
            throw new Error("You need at least 1 token to reschedule this session again. The first reschedule is free, but additional reschedules cost 1 token each.");
          }
          
          // Deduct 1 token for the reschedule fee
          const { error: spendError } = await supabase.rpc('spend_credits', {
            p_user_id: studentProfile.user_id,
            p_credits_to_spend: 1
          });
          
          if (spendError) {
            throw new Error("Failed to process reschedule fee. Please try again.");
          }
        }
        
        // For reschedules, validate the new time first
        const scheduledDate = format(selectedTimeSlot.date, 'yyyy-MM-dd');
        const scheduledDateTime = selectedTimeSlot.utcDateTime || new Date(selectedTimeSlot.date);
        
        // CRITICAL: Validate booking against mentor availability AND 24-hour rule
        const { data: validationResult, error: validationError } = await supabase.functions.invoke('validate-session-booking', {
          body: {
            mentorId: selectedMentor.id,
            scheduledDate: scheduledDate,
            scheduledStartTime: selectedTimeSlot.start_time,
            scheduledEndTime: selectedTimeSlot.end_time,
            utcDateTime: scheduledDateTime.toISOString()
          }
        });

        if (validationError || !validationResult?.valid) {
          const reason = validationResult?.reason || validationError?.message || 'This time slot is not available';
          throw new Error(reason);
        }
        
        // Update the existing session
        const { data: updatedSession, error: updateError } = await supabase
          .from('sessions')
          .update({
            notes: bookingNotes,
            scheduled_date: scheduledDate,
            scheduled_start_time: selectedTimeSlot.start_time,
            scheduled_end_time: selectedTimeSlot.end_time,
            scheduled_at: scheduledDateTime.toISOString(),
            reschedule_count: (sessionToReschedule.reschedule_count || 0) + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', sessionToReschedule.id)
          .select()
          .single();

        if (updateError) {
          throw new Error("Failed to reschedule session. Please try again.");
        }
        sessionData = updatedSession;
      } else {
        // For new bookings, spend tokens ONLY if not using a program session
        if (!usingProgramSession) {
          const tokensToSpend = selectedMentor ? (selectedMentor.hourly_rate / 100) : 10;
          const { data: spendResult, error: spendError } = await supabase.rpc('spend_credits', {
            p_user_id: studentProfile?.user_id,
            p_credits_to_spend: tokensToSpend
          });

          if (spendError || !spendResult) {
            throw new Error("Failed to process token payment. Please try again.");
          }
        }

        // Create the new session
        const scheduledDate = format(selectedTimeSlot.date, 'yyyy-MM-dd');
        // Use the UTC datetime that was properly calculated in the booking flow
        const scheduledDateTime = selectedTimeSlot.utcDateTime || new Date(selectedTimeSlot.date);
        
        // CRITICAL: Validate booking against mentor availability on server-side
        const { data: validationResult, error: validationError } = await supabase.functions.invoke('validate-session-booking', {
          body: {
            mentorId: selectedMentor.id,
            scheduledDate: scheduledDate,
            scheduledStartTime: selectedTimeSlot.start_time,
            scheduledEndTime: selectedTimeSlot.end_time,
            utcDateTime: scheduledDateTime.toISOString()
          }
        });

        if (validationError || !validationResult?.valid) {
          const reason = validationResult?.reason || validationError?.message || 'This time slot is not available';
          
          // Refund the tokens we just spent (only if we actually spent tokens)
          if (!usingProgramSession) {
            const tokensToSpend = selectedMentor ? (selectedMentor.hourly_rate / 100) : 10;
            await supabase.rpc('process_credit_purchase', {
              p_user_id: studentProfile?.user_id,
              p_credits: tokensToSpend,
              p_stripe_session_id: 'refund_' + Date.now(),
              p_amount_paid: 0
            });
          }
          
          throw new Error(reason);
        }
        
        // Calculate confirmation deadline (24 hours from now)
        const confirmationDeadline = new Date();
        confirmationDeadline.setHours(confirmationDeadline.getHours() + 24);

        const { data: newSession, error: sessionError } = await supabase
          .from('sessions')
          .insert({
            student_id: studentProfile?.user_id,
            mentor_id: selectedMentor.id,
            notes: bookingNotes,
            scheduled_date: scheduledDate,
            scheduled_start_time: selectedTimeSlot.start_time,
            scheduled_end_time: selectedTimeSlot.end_time,
            scheduled_at: scheduledDateTime.toISOString(),
            status: 'pending',
            confirmation_deadline: confirmationDeadline.toISOString(),
            reschedule_count: 0,
            program_enrollment_id: usingProgramSession ? selectedProgramEnrollmentId : null
          })
          .select()
          .single();

        if (sessionError) {
          console.error('Session creation error:', sessionError);
          // Refund the tokens we just spent (only if we actually spent tokens)
          if (!usingProgramSession) {
            const tokensToSpend = selectedMentor ? (selectedMentor.hourly_rate / 100) : 10;
            await supabase.rpc('process_credit_purchase', {
              p_user_id: studentProfile?.user_id,
              p_credits: tokensToSpend,
              p_stripe_session_id: 'refund_' + Date.now(),
              p_amount_paid: 0
            });
          }
          throw new Error('Failed to create session. Please try again.');
        }
        sessionData = newSession;
      }

      // Send in-app notifications
      try {
        await supabase.functions.invoke('create-session-notifications', {
          body: {
            sessionId: sessionData.id,
            studentId: studentProfile?.user_id,
            mentorId: selectedMentor.id,
          },
        });
      } catch (notificationError) {
        console.error('Failed to send notifications:', notificationError);
        // Don't fail the booking if notifications fail
      }

      // Send email notification to mentor about the new booking
      if (!sessionToReschedule) { // Only send for new bookings, not reschedules
        try {
          await supabase.functions.invoke('send-mentor-notification', {
            body: {
              type: 'session_booked',
              sessionId: sessionData.id
            }
          });
        } catch (emailError) {
          console.error('Error sending mentor notification email:', emailError);
          // Don't fail the booking if email fails
        }

        // Send admin notification about the new booking
        try {
          await supabase.functions.invoke('send-admin-notification', {
            body: {
              type: 'session_booking',
              userEmail: studentProfile?.email || '',
              additionalInfo: {
                studentName: `${studentProfile?.first_name} ${studentProfile?.last_name}`,
                mentorName: `${selectedMentor.first_name} ${selectedMentor.last_name}`,
                scheduledDate: format(selectedTimeSlot.date, 'MMM dd, yyyy'),
                scheduledTime: selectedTimeSlot.start_time,
                durationMinutes: 60,
                creditsUsed: usingProgramSession ? 'Program Session' : `${selectedMentor.hourly_rate / 100} tokens`
              }
            }
          });
        } catch (emailError) {
          console.error('Error sending admin notification:', emailError);
          // Don't fail the booking if email fails
        }
      }

      // Send calendar invite to both mentor and student
      try {
        if (selectedMentor.email && studentProfile?.email) {
          await supabase.functions.invoke('send-calendar-invite', {
            body: {
              sessionId: sessionData.id,
              mentorName: `${selectedMentor.first_name} ${selectedMentor.last_name}`,
              mentorEmail: selectedMentor.email,
              studentName: `${studentProfile?.first_name} ${studentProfile?.last_name}`,
              studentEmail: studentProfile?.email,
              scheduledAt: sessionData.scheduled_at, // Use the UTC timestamp from the database
              durationMinutes: sessionData.duration_minutes || 60,
              notes: bookingNotes,
              mentorTimezone: selectedTimeSlot.mentorTimezone || selectedMentor.timezone || 'America/Chicago',
              studentTimezone: getUserTimezone()
            }
          });
        } else {
          console.warn('Cannot send calendar invite: missing email addresses', {
            mentorEmail: selectedMentor.email,
            studentEmail: studentProfile?.email
          });
        }
      } catch (emailError) {
        console.error('Error sending calendar invite:', emailError);
        // Don't fail the booking if email fails
      }

      // Refresh tokens and sessions
      refetchCredits();
      const fetchSessions = async () => {
        const { data } = await supabase
          .from('sessions')
          .select(`
            *,
            mentors (id, first_name, last_name, specialty, profile_image_url, email, hourly_rate),
            session_reviews (id, rating, review_text)
          `)
          .eq('student_id', studentProfile?.user_id)
          .neq('status', 'cancelled') // Filter out cancelled sessions
          .order('scheduled_at', { ascending: true });
        setSessions(data || []);
      };

      fetchSessions();

      toast({
        title: sessionToReschedule ? "Session Rescheduled!" : "Session Booked!",
        description: `Your session with ${selectedMentor?.first_name} ${selectedMentor?.last_name} has been ${sessionToReschedule ? 'rescheduled' : 'scheduled'} for ${selectedTimeSlot.date.toLocaleDateString()} at ${selectedTimeSlot.start_time}.`,
      });
      
      // Clear booking data and state
      clearBookingData();
      setSelectedMentor(null);
      setBookingNotes("");
      setSelectedTimeSlot(null);
      setSessionToReschedule(null);
    } catch (error: any) {
      console.error('Error booking session:', error);
      toast({
        title: sessionToReschedule ? "Reschedule Failed" : "Booking Failed",
        description: error.message || `Failed to ${sessionToReschedule ? 'reschedule' : 'book'} session. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsBookingInProgress(false);
    }
  };

  const handleDismissPreparationNotice = () => {
    safeSetItem('preparation_notice_dismissed', 'true');
    setIsPreparationNoticeDismissed(true);
    toast({
      title: "Notice Dismissed",
      description: "You won't see this message again.",
    });
  };

  const handleReviewNotificationClick = async (sessionId: string) => {
    try {
      // Fetch the session details for the review modal
      const { data: session, error } = await supabase
        .from('sessions')
        .select(`
          *,
          mentors (first_name, last_name, specialty)
        `)
        .eq('id', sessionId)
        .eq('status', 'completed')
        .single();

      if (error || !session) {
        toast({
          title: "Session Not Found",
          description: "Could not find the completed session for review.",
          variant: "destructive",
        });
        return;
      }

      // Check if review already exists
      const { data: existingReview } = await supabase
        .from('session_reviews')
        .select('id')
        .eq('session_id', sessionId)
        .eq('student_id', session.student_id)
        .single();

      if (existingReview) {
        toast({
          title: "Already Reviewed",
          description: "You have already reviewed this session.",
        });
        return;
      }

      // Open the review modal
      setReviewModalSession(session);
    } catch (error) {
      console.error('Error opening review modal:', error);
      toast({
        title: "Error",
        description: "Failed to open review form. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background border-b border-border w-full overflow-x-hidden">
        <div className="container mx-auto px-2 sm:px-4 max-w-full overflow-x-hidden">
          <div className="flex items-center justify-between py-2 md:py-4 gap-2">
            <div className="flex items-center space-x-2 md:space-x-4 min-w-0 flex-shrink overflow-hidden">
              <div className="flex items-center gap-3">
                <img 
                  src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                  alt="Ascendry" 
                  className="h-8 md:h-10 w-auto"
                />
                <span className="hidden md:block text-2xl font-bold text-glow">
                  Ascendry
                </span>
              </div>
              <h1 className="text-lg md:text-2xl font-bold truncate">Student Dashboard</h1>
            </div>
            <div className="flex items-center space-x-1 md:space-x-4 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm" 
                asChild 
                className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                title="Join our Discord community"
              >
                <a href="https://discord.gg/Ump5j4TYZu" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  <span className="hidden md:inline">Discord</span>
                </a>
              </Button>
              <NotificationBell onReviewNotificationClick={handleReviewNotificationClick} />
              <Button variant="ghost" size="sm" asChild className="hidden md:flex">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="md:hidden">
                <Link to="/">
                  <Home className="w-4 h-4" />
                </Link>
              </Button>
              <span className="text-muted-foreground hidden lg:inline">
                {studentProfile ? `${studentProfile.first_name} ${studentProfile.last_name}` : 'Loading...'}
              </span>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="hidden md:flex">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="md:hidden">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 space-y-6 sm:space-y-8 max-w-full">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between overflow-hidden">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold truncate">Welcome Back</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1 truncate">Manage your learning journey</p>
          </div>
        </div>

        {/* Token Balance Display */}
        <Card className="obsidian-card border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Available Tokens</p>
                  <p className="text-3xl font-bold text-primary">
                    {creditsLoading ? '...' : credits.balance}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setIsPurchaseModalOpen(true)} 
                size="lg"
                className="energy-button w-full sm:w-auto"
              >
                Buy More Tokens
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recommended Mentors Section */}
        <RecommendedMentorsSection onBookMentor={handleBookSession} />

        {/* Upcoming Sessions Section - Prioritized for visibility */}
        <section className="overflow-hidden">
          <h2 className="text-xl font-semibold mb-4">Upcoming Sessions</h2>
          <Card className="overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                {sessions.filter(s => {
                  if (!['pending', 'scheduled'].includes(s.status)) return false;
                  const sessionTime = new Date(s.scheduled_at);
                  const now = new Date();
                  // Show sessions until they end (scheduled_at + duration)
                  const sessionEndTime = new Date(sessionTime.getTime() + (s.duration_minutes || 60) * 60 * 1000);
                  return sessionEndTime > now;
                }).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No upcoming sessions scheduled
                  </div>
                ) : (
                  sessions
                    .filter(s => {
                      if (!['pending', 'scheduled'].includes(s.status)) return false;
                      const sessionTime = new Date(s.scheduled_at);
                      const now = new Date();
                      // Show sessions until they end (scheduled_at + duration)
                      const sessionEndTime = new Date(sessionTime.getTime() + (s.duration_minutes || 60) * 60 * 1000);
                      return sessionEndTime > now;
                    })
                    .map((session) => {
                       const sessionDate = new Date(session.scheduled_at);
                       const now = new Date();
                       const hoursUntilSession = (sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60);
                       const canReschedule = hoursUntilSession > 24;
                      
                       return (
                        <div key={session.id} className="border rounded-lg p-3 sm:p-4 space-y-3 overflow-hidden">
                          <div className="flex items-start justify-between gap-2 overflow-hidden">
                           <div className="flex items-center space-x-3 min-w-0 flex-1 overflow-hidden">
                             <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                               {session.mentors?.profile_image_url ? (
                                 <img 
                                   src={session.mentors.profile_image_url} 
                                   alt={`${session.mentors.first_name} ${session.mentors.last_name}`}
                                   className="w-full h-full rounded-full object-cover"
                                 />
                               ) : (
                                 <span className="text-primary font-medium">
                                   {session.mentors?.first_name?.[0] || 'M'}
                                 </span>
                               )}
                             </div>
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <h4 className="font-medium truncate">
                                 {session.mentors?.first_name} {session.mentors?.last_name}
                               </h4>
                                <p className="text-sm text-muted-foreground truncate">{session.mentors?.specialty}</p>
                                <p className="text-xs text-muted-foreground truncate">Contact: {session.mentors?.email || 'Not available'}</p>
                             </div>
                           </div>
                            <Badge variant={session.status === 'pending' ? 'secondary' : 'outline'} className="flex-shrink-0 whitespace-nowrap">
                              {session.status === 'pending' ? 'Pending Confirmation' : 'Confirmed'}
                            </Badge>
                          </div>
                          {session.status === 'pending' && session.confirmation_deadline && (
                            <div className="text-sm text-amber-600 font-medium bg-amber-50 dark:bg-amber-950 p-2 rounded">
                              ⏰ Waiting for mentor confirmation (by {new Date(session.confirmation_deadline).toLocaleString()})
                            </div>
                          )}
                          <div className="text-sm space-y-1 overflow-hidden">
                            <p className="break-words"><strong>Date:</strong> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))}</p>
                            <p className="break-words"><strong>Time:</strong> {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} - {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(new Date(session.scheduled_at).getTime() + ((session.duration_minutes || 60) * 60000)))} {getTimezoneAbbr(getStoredTimezone())}</p>
                            <p className="break-words"><strong>Notes:</strong> {session.notes}</p>
                         </div>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {/* Join Video Call Button - Always visible for scheduled sessions */}
                            {session.status === 'scheduled' && (() => {
                              const sessionDate = new Date(session.scheduled_at);
                              const now = new Date();
                              const minutesUntilSession = (sessionDate.getTime() - now.getTime()) / (1000 * 60);
                              const sessionEndTime = new Date(sessionDate.getTime() + (session.duration_minutes || 60) * 60000);
                              const minutesSinceEnd = (now.getTime() - sessionEndTime.getTime()) / (1000 * 60);
                              
                              // Can join 15 minutes before session or during session, but not after it ends
                              const canJoin = minutesUntilSession <= 15 && minutesSinceEnd < 0;
                              
                              return (
                                <Button
                                  size="sm"
                                  variant={canJoin ? "default" : "outline"}
                                  onClick={() => navigate(`/session/${session.id}`)}
                                  disabled={!canJoin}
                                  className={cn(
                                    "flex items-center gap-2 font-medium",
                                    canJoin && "bg-green-600 hover:bg-green-700 text-white animate-pulse"
                                  )}
                                  title={
                                    !canJoin 
                                      ? minutesUntilSession > 15
                                        ? `Available 15 min before session (${Math.floor(Math.abs(minutesUntilSession / 60))}h ${Math.floor(Math.abs(minutesUntilSession % 60))}m)` 
                                        : "Session has ended"
                                      : "Click to join video call"
                                  }
                                >
                                  <Video className="w-4 h-4" />
                                  {canJoin ? "🔴 Join Video Call" : `Join in ${Math.floor(minutesUntilSession / 60)}h ${Math.floor(minutesUntilSession % 60)}m`}
                                </Button>
                              );
                            })()}
                           
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => downloadCalendarFile(session.id)}
                             className="flex items-center gap-2"
                           >
                             <Calendar className="w-4 h-4" />
                             Add to Calendar
                           </Button>
                            <Button
                              size="sm"
                              variant="outline"
                               disabled={!canReschedule}
                               onClick={() => {
                                 if (canReschedule) {
                                   setSessionToReschedule(session);
                                   setSelectedMentor(session.mentors);
                                   setBookingNotes(session.notes || "");
                                 }
                               }}
                                title={
                                  hoursUntilSession <= 24 
                                    ? "Cannot reschedule within 24 hours of session" 
                                    : (session.reschedule_count || 0) === 0
                                      ? "Reschedule session (first reschedule is free)"
                                      : "Reschedule session (costs 1 token)"
                                }
                              className={`flex items-center gap-2 ${!canReschedule ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <Clock className="w-4 h-4" />
                              Reschedule
                            </Button>
                           <Button
                             size="sm"
                             variant="outline"
                             asChild
                             className="flex items-center gap-2"
                           >
                             <a href={`mailto:${session.mentors?.email || ''}?subject=Session Question - ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))}`}>
                               <MessageSquare className="w-4 h-4" />
                               Message Mentor
                             </a>
                           </Button>
                           <Button
                             size="sm"
                             variant="destructive"
                             onClick={() => handleCancelSession(session)}
                             className="flex items-center gap-2"
                           >
                             <X className="w-4 h-4" />
                             Cancel
                           </Button>
                         </div>
                       </div>
                      );
                    })
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Completed Sessions Section - Prioritized for Easy Re-booking */}
        <section className="overflow-hidden">
          <h2 className="text-xl font-semibold mb-4">Completed Sessions</h2>
          <Card className="overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                {sessions.filter(s => {
                  if (s.status === 'completed') return true;
                  // Also show scheduled sessions that have ended (auto-completed)
                  if (s.status === 'scheduled') {
                    const sessionEndTime = new Date(new Date(s.scheduled_at).getTime() + (s.duration_minutes || 60) * 60 * 1000);
                    return sessionEndTime < new Date();
                  }
                  return false;
                }).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No completed sessions yet
                  </div>
                ) : (
                  sessions
                    .filter(s => {
                      if (s.status === 'completed') return true;
                      // Also show scheduled sessions that have ended (auto-completed)
                      if (s.status === 'scheduled') {
                        const sessionEndTime = new Date(new Date(s.scheduled_at).getTime() + (s.duration_minutes || 60) * 60 * 1000);
                        return sessionEndTime < new Date();
                      }
                      return false;
                    })
                    .slice(0, 5)
                    .map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">
                                {session.mentors?.first_name} {session.mentors?.last_name}
                              </h4>
                              <p className="text-sm text-muted-foreground">{session.mentors?.specialty}</p>
                            </div>
                            <Badge variant="default" className="bg-mentor-primary/10 text-mentor-primary">
                              Completed
                            </Badge>
                          </div>
                           <div className="text-sm space-y-1">
                             <p><strong>Date:</strong> {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: getStoredTimezone() }).format(new Date(session.completed_at || session.scheduled_at))}</p>
                             <p><strong>Duration:</strong> {session.duration_minutes} minutes</p>
                             {session.notes && <p><strong>Notes:</strong> {session.notes}</p>}
                           </div>
                             <div className="mt-2">
                               <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                                 <Button
                                   size="sm"
                                   variant="default"
                                   onClick={() => handleBookSession(session.mentors)}
                                   className="w-full sm:w-auto flex items-center gap-1"
                                 >
                                   <Calendar className="w-3 h-3" />
                                   Book Again
                                 </Button>
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => session.session_reviews && session.session_reviews.length > 0 
                                     ? undefined 
                                     : setReviewModalSession(session)
                                   }
                                   disabled={session.session_reviews && session.session_reviews.length > 0}
                                   className={`w-full sm:w-auto ${session.session_reviews && session.session_reviews.length > 0 
                                     ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-50' 
                                     : 'text-yellow-600 border-yellow-300 hover:bg-yellow-50'
                                   }`}
                                 >
                                   {session.session_reviews && session.session_reviews.length > 0 
                                     ? '✓ Rated!' 
                                     : '⭐ Rate Session'
                                   }
                                 </Button>
                                 <Button
                                   size="sm"
                                   variant="outline"
                                   onClick={() => setSessionToReport(session)}
                                   className="col-span-2 sm:col-span-1 w-full sm:w-auto flex items-center gap-1"
                                 >
                                   <AlertTriangle className="w-3 h-3" />
                                   Report Issue
                                 </Button>
                                </div>
                              </div>
                         </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
        </section>

        {/* Discovery & Booking Section */}
        <section className="overflow-hidden">
          <h2 className="text-xl font-semibold mb-4">Discovery & Booking</h2>
          
          {/* Swipe indicator */}
          <div className="text-xl text-muted-foreground flex items-center justify-center gap-2 mb-1">
            <span>Swipe</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>

          <div className="space-y-6 overflow-hidden">
            {/* Mentor Carousel */}
            <MentorCarousel mentors={realMentors} showTitle={false} onBookMentor={handleBookSession} />
            
            {/* View All Mentors Button */}
            <div className="text-center">
              <Button variant="outline" asChild>
                <Link to="/mentors">
                  View All Mentors
                </Link>
              </Button>
            </div>
          </div>
        </section>

          {/* Account Overview Section */}
          <StudentProgramEnrollments />

          <section>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tokens */}
            <div className="lg:col-span-2 min-w-0">
              <Card className="h-full flex flex-col overflow-hidden">
                <CardHeader className="overflow-hidden">
                  <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <span className="flex items-center min-w-0">
                      <CreditCard className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="truncate">Available Tokens</span>
                    </span>
                    <Button onClick={() => setIsPurchaseModalOpen(true)} size="sm" className="w-full sm:w-auto flex-shrink-0">
                      Buy More Tokens
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-center">
                  <div className="text-3xl font-bold text-primary mb-2">
                    {creditsLoading ? '...' : credits.balance}
                  </div>
                  <p className="text-muted-foreground mb-3">Tokens available for mentoring sessions</p>
                  {!creditsLoading && (
                    <div className="text-xs text-muted-foreground mb-4">
                      Total purchased: {credits.total_purchased} • Spent: {credits.total_spent}
                    </div>
                  )}
                  
                  {/* Single tip to balance height */}
                  <div className="mt-auto">
                    <div className="text-sm text-muted-foreground">
                      💡 <strong>Tip:</strong> Tokens are applied toward mentor sessions based on their hourly rates
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="space-y-4 min-w-0">
              <Card className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Sessions Completed</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{sessions.filter(s => s.status === 'completed').length}</div>
                  <p className="text-xs text-muted-foreground">
                    {sessions.filter(s => s.status === 'completed').length === 0 ? 'No sessions yet' : 'Sessions completed'}
                  </p>
                </CardContent>
              </Card>

              {/* Discord Community Card */}
              <Card className="obsidian-card border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-indigo-900/20 overflow-hidden">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-12 h-12 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-purple-300 mb-2">Join Our Community</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Connect with fellow students and mentors on Discord
                    </p>
                    <Button 
                      asChild 
                      size="sm" 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <a href="https://discord.gg/Ump5j4TYZu" target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Join Discord
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Account Settings Section */}
        <section className="overflow-hidden">
          <Collapsible open={isAccountSettingsOpen} onOpenChange={setIsAccountSettingsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full text-left hover:text-primary transition-colors">
              <h2 className="text-xl font-semibold">Account Settings</h2>
              <ChevronDown className={`h-5 w-5 transition-transform ${isAccountSettingsOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-6">
                <AccountDeactivation />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </section>
      </div>

      {/* Modals */}
      <TokenPurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        onPurchase={handlePurchase}
      />

      {selectedMentor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-hidden">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold break-words">{sessionToReschedule ? 'Reschedule' : 'Book'} Session with {selectedMentor.first_name} {selectedMentor.last_name}</h2>
                  <p className="text-sm sm:text-base text-muted-foreground break-words">{selectedMentor.specialty}</p>
                  {sessionToReschedule && (
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                        <div>
                          <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">
                            Rescheduling your session from {new Date(sessionToReschedule.scheduled_at).toLocaleDateString()} at {formatTime12Hour(sessionToReschedule.scheduled_start_time)}
                          </p>
                          {(sessionToReschedule.reschedule_count || 0) === 0 ? (
                            <p className="text-blue-700 dark:text-blue-300 text-sm">
                              <strong>Note:</strong> Your first reschedule is free! Additional reschedules will cost 1 token each.
                            </p>
                          ) : (
                            <p className="text-amber-700 dark:text-amber-300 text-sm bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded p-2 mt-2">
                              <AlertTriangle className="w-4 h-4 inline mr-1" />
                              <strong>Reschedule Fee:</strong> This reschedule will cost 1 token since you've already rescheduled once.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => {
                  setSelectedMentor(null);
                  setSessionToReschedule(null);
                  setBookingNotes("");
                  setSelectedTimeSlot(null);
                }}>
                  ✕
                </Button>
              </div>

              <StreamlinedBookingFlow
                mentorId={selectedMentor.id}
                mentorName={`${selectedMentor.first_name} ${selectedMentor.last_name}`}
                onTimeSlotSelect={setSelectedTimeSlot}
                selectedSlot={selectedTimeSlot}
              />

              {/* Program Session Selector */}
              <ProgramSessionSelector
                mentorId={selectedMentor.id}
                onProgramSelect={setSelectedProgramEnrollmentId}
                selectedProgramId={selectedProgramEnrollmentId}
              />

              <div className="space-y-4 min-w-0">
                <div>
                  <Label htmlFor="booking-notes">Session Notes</Label>
                  <Textarea
                    id="booking-notes"
                    placeholder="What would you like to discuss in this session?"
                    value={bookingNotes}
                    onChange={(e) => setBookingNotes(e.target.value)}
                    required
                    className="resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSelectedMentor(null);
                      setSessionToReschedule(null);
                      setBookingNotes("");
                      setSelectedTimeSlot(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={confirmBooking}
                    disabled={!selectedTimeSlot || !bookingNotes.trim() || isBookingInProgress}
                    className="flex-1 text-sm sm:text-base"
                  >
                    <span className="truncate">
                      {isBookingInProgress ? (sessionToReschedule ? 'Rescheduling...' : 'Booking...') : (sessionToReschedule ? `Confirm Reschedule ${(sessionToReschedule.reschedule_count || 0) === 0 ? '(Free)' : '(1 Token)'}` : `Confirm Booking (${selectedMentor ? (selectedMentor.hourly_rate / 100) : 10} Tokens)`)}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reviewModalSession && (
        <SessionReviewModal
          isOpen={true}
          session={reviewModalSession}
          onClose={() => setReviewModalSession(null)}
          onReviewSubmitted={() => {
            setReviewModalSession(null);
            // Refresh sessions to show updated data
     const fetchSessions = async () => {
       const { data } = await supabase
         .from('sessions')
         .select(`
           *,
           mentors (id, first_name, last_name, specialty, profile_image_url, email, hourly_rate),
           session_reviews (id, rating, review_text)
         `)
         .eq('student_id', studentProfile?.user_id)
         .neq('status', 'cancelled')
         .order('scheduled_at', { ascending: true });
       setSessions(data || []);
     };
            fetchSessions();
          }}
        />
      )}

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this session? 
              {getSessionDate() ? (
                <span className="text-destructive font-medium">
                  {" "}Since this is a day-of cancellation, your credits will be forfeited and cannot be refunded.
                </span>
              ) : (
                " Your credits will be refunded to your account."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Session</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmCancelSession} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {getSessionDate() ? "Cancel & Forfeit Credits" : "Cancel & Refund Credits"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



      {sessionToReport && (
        <ReportSessionModal
          isOpen={!!sessionToReport}
          onClose={() => setSessionToReport(null)}
          sessionId={sessionToReport.id}
          mentorName={`${sessionToReport.mentors?.first_name || ''} ${sessionToReport.mentors?.last_name || ''}`}
          sessionDate={new Date(sessionToReport.completed_at || sessionToReport.scheduled_at).toLocaleDateString()}
          userRole="student"
        />
      )}

      {/* Zeus AI Chatbot for Student Dashboard */}
      <AIMentorChatbot 
        userRole="student" 
        isStudentDashboard={true}
        onBookMentor={(mentorId) => {
          const mentor = realMentors.find(m => m.id === mentorId);
          if (mentor) {
            handleBookSession(mentor);
          }
        }}
      />
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default StudentDashboard;
